// SSO packet implementation for TypeScript
// implementation for C++: https://git.woa.com/crdc/base/blob/master/liteav_base/to_delete/completion_report_channel/sso_packet.cc
// ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/1020396022124285235

import { isString } from '../utils';
import { Buffer, writUint32, readUint32 } from './buffer';

let seqNumber = 0;

interface SsoRequestPacket {
  // 基本信息
  version: number; // 协议版本
  encryption: number; // 加密算法. 0: 不加密; 1: 用 uin 对应 KEY 做 TEA 加密; 2: 用空 key (16 个 0) 做 TEA 加密; 3: 不加密但校验 key
  d2: string; // 终端和 sso 的身份凭证
  d2Len: number; // d2 的长度(计算时需要包含长度字段本身的4字节),不需要 d2 时,则该值填4
  uinType: number; // 帐号类型. 0 表示 uin 是 QQ 号, 1 表示 uin 是 mail 帐号
  uin: string; // 用户号码
  uinLen: number;  // 用户号码长度
  // 请求头
  reqHead: SsoRequestHeader;
  // 业务自定义的数据包
  busiBuff: string | Uint8Array;
}

interface SsoRequestHeader {
  seqNumber: number; // uin 的递增序列号
  appId: number; // SDK Appid
  appidAtThird: Uint8Array; // 16 bytes: 4 bytes SDK Appid + 12 bytes reserved
  a2: string; // 访问即通的身份凭证
  a2Len: number; // a2 的长度
  serviceCmd: string; // 形式为 *.*, 其中第一个 '*' 表示主业务
  serviceCmdLen: number; // serviceCmd 长度
  cookie: string; // cookie
  cookieLen: number; // cookie 长度
  imei: string; // 手机 imei 码
  imeiLen: number; // imei 长度
  ksid: string; // 安全 ksid
  ksidLen: number;  // 安全 ksid 长度
  clientVersionInfo: string; // 终端版本信息
  clientVersionInfoLen: number; // 终端版本信息长度, 该字段只有两个字节
}

interface SsoResponseHeader {
  respHeadLen: number; // 接入层的 Header 数据长度
  seqNumber: number;  // Uin 的递增序列号
  retCode: number;  // SSO 的处理结果（比如key超时）
  retStr: string; // SSO 处理失败描述
  retStrLen: number; // SSO 处理失败描述长度(计算时需要包含长度字段本身的4字节）
  serviceCmd: string; // 形式为 *.*, 其中第一个'*'表示主业务
  serviceCmdLen: number; // serviceCmd 长度
  cookie: string; // cookie
  cookieLen: number; // cookie 长度
  flag: number; // ver 8 专有, 回复给终端的标志
}

interface SsoResponsePacket {
  len: number; // 整包长度
  version: number; // 协议版本
  encryption: number; // 加密算法
  uinType: number;
  uin: string;
  uinLen: number;
  respHead: SsoResponseHeader;
  busiBuff: Uint8Array;
}


// TEA 加密用到的常量
export const DELTA = 0x9e3779b9;
export const ROUNDS = 16;
export const LOG_ROUNDS = 4;
export const SALT_LEN = 2;
export const ZERO_LEN = 7;

function initSSOPacket(sdkAppId: number, busiBuff: string | Uint8Array, seqNumber, serviceCmd = 'AVQualityReportSvc.C2S', version = 2000, encryption = 2, uinType = 30): SsoRequestPacket {
  return {
    version,
    encryption,
    d2: '',
    d2Len: 0,
    uinType,
    uin: '',
    uinLen: 0,
    reqHead: {
      seqNumber,
      appId: sdkAppId,
      appidAtThird: new Uint8Array(0),
      a2: '',
      a2Len: 0,
      serviceCmd,
      serviceCmdLen: 0,
      cookie: '',
      cookieLen: 0,
      imei: '',
      imeiLen: 0,
      ksid: '',
      ksidLen: 0,
      clientVersionInfo: '',
      clientVersionInfoLen: 0
    },
    busiBuff
  };
}

export function encodeSSOPackage(busiBuff: string | Uint8Array, sdkAppId: number) {
  const buffer = new Buffer();
  const config = initSSOPacket(sdkAppId, busiBuff, seqNumber);

  seqNumber = (seqNumber + 1) & 0x7fffffff;

  // 1. 基本信息
  buffer.writeInt32(0); // Len - 先写入占位符
  buffer.writeInt32(config.version); // 协议版本
  buffer.writeByte(config.encryption);  // 加密算法

  const d2Bytes = (new TextEncoder).encode(config.d2); // D2 长度
  buffer.writeInt32(d2Bytes.length + 4); // 包含长度字段的4字节
  if (d2Bytes) buffer.writeBytes(d2Bytes);

  buffer.writeByte(config.uinType); // UinType - 账号类型

  const uinBytes = new TextEncoder().encode(config.uin); // UinLen 和 Uin
  buffer.writeInt32(uinBytes.length + 4); // 包含长度字段的4字节
  if (uinBytes.length) buffer.writeBytes(uinBytes);

  // 2. ReqHead + BusiBuff
  const tempBuffer = new Buffer();

  tempBuffer.writeInt32(0); // ReqHeadLen 占位符
  tempBuffer.writeInt32(config.reqHead.seqNumber); // SeqNo
  tempBuffer.writeInt32(config.reqHead.appId); // AppId

  tempBuffer.writeByte((config.reqHead.appId >>> 24) & 0xff); // LC - 前4字节为 sdkAPPID, 后12字节保留
  tempBuffer.writeByte((config.reqHead.appId >>> 16) & 0xff);
  tempBuffer.writeByte((config.reqHead.appId >>> 8) & 0xff);
  tempBuffer.writeByte(config.reqHead.appId & 0xff);
  for (let i = 4; i < 16; i++) {
    tempBuffer.writeByte(0);
  }

  const a2Bytes = (new TextEncoder).encode(config.reqHead.a2); // A2Len 和 A2
  tempBuffer.writeInt32(a2Bytes.length + 4);
  if (a2Bytes.length) tempBuffer.writeBytes(a2Bytes);

  const serviceCmdBytes = new TextEncoder().encode(config.reqHead.serviceCmd); // ServiceCmdLen 和 ServiceCmd
  tempBuffer.writeInt32(serviceCmdBytes.length + 4);
  if (serviceCmdBytes.length) tempBuffer.writeBytes(serviceCmdBytes);

  const cookieBytes = new TextEncoder().encode(config.reqHead.cookie); // CookieLen 和 Cookie
  tempBuffer.writeInt32(cookieBytes.length + 4);
  if (cookieBytes.length) tempBuffer.writeBytes(cookieBytes);

  const imeiBytes = new TextEncoder().encode(config.reqHead.imei); // IMEILen 和 IMEI
  tempBuffer.writeInt32(imeiBytes.length + 4);
  if (imeiBytes.length) tempBuffer.writeBytes(imeiBytes);

  const ksidBytes = new TextEncoder().encode(config.reqHead.ksid); // KSIDLen 和 KSID
  tempBuffer.writeInt32(ksidBytes.length + 4);
  if (ksidBytes.length) tempBuffer.writeBytes(ksidBytes);

  const clientVerInfoBytes = new TextEncoder().encode(config.reqHead.clientVersionInfo); // ClientVerInfoLen 和 ClientVerInfo
  tempBuffer.writeInt16(clientVerInfoBytes.length + 2);
  if (clientVerInfoBytes.length) tempBuffer.writeBytes(clientVerInfoBytes);

  const reqHeadLength = tempBuffer.length; // 计算 ReqHeadLen
  tempBuffer.data[0] = (reqHeadLength >>> 24) & 0xff;
  tempBuffer.data[1] = (reqHeadLength >>> 16) & 0xff;
  tempBuffer.data[2] = (reqHeadLength >>> 8) & 0xff;
  tempBuffer.data[3] = reqHeadLength & 0xff;

  if (isString(busiBuff)) busiBuff = new TextEncoder().encode(busiBuff); // 业务数据
  tempBuffer.writeInt32(busiBuff.length + 4);
  if (busiBuff.length) tempBuffer.writeBytes(busiBuff);

  // 3.加密
  let encryptedPart = new Uint8Array(tempBuffer.data);
  let encryptKey = null;
  if (config.encryption === 1) {
    encryptKey = (new TextEncoder).encode(config.uin); // 使用 UIN 作为 KEY 进行 TEA 加密
  } else if (config.encryption === 2) {
    encryptKey = new Uint8Array(16); // // 使用空 KEY 进行 TEA 加密
  }
  if (encryptKey) encryptedPart = encrypt(encryptedPart, encryptKey);
  buffer.writeBytes(encryptedPart);

  // 4. 计算 SSO 包长度
  const result = new Uint8Array(buffer.data);
  const totalLength = result.length;
  result[0] = (totalLength >>> 24) & 0xff;
  result[1] = (totalLength >>> 16) & 0xff;
  result[2] = (totalLength >>> 8) & 0xff;
  result[3] = totalLength & 0xff;

  return result;
}

function encrypt(data: Uint8Array, key: Uint8Array) {
  const inputLength = data.length;
  // 计算padLen
  const padSaltBodyZeroLen = inputLength + 1 + SALT_LEN + ZERO_LEN;
  let padLen = padSaltBodyZeroLen % 8;
  if (padLen) {
    padLen = 8 - padLen;
  }
  // 计算总输出长度
  const totalLen = inputLength + 1 + SALT_LEN + ZERO_LEN + padLen;
  // const outputLength = Math.ceil(totalLen / 8) * 8;

  const outputBuff = new Uint8Array(totalLen);
  let outputPos = 0;

  // 初始化变量
  const srcBuf = new Uint8Array(8);
  const ivPlain = new Uint8Array(8);
  const ivCrypt = new Uint8Array(8);
  let srcIndex = 0;

  // 生成第一个字节：padLen + 随机数
  srcBuf[0] = (Math.floor(Math.random() * 256) & 0xf8) | padLen;
  srcIndex = 1;

  // 添加padding
  for (let i = 0; i < padLen; i++) {
    srcBuf[srcIndex] = Math.floor(Math.random() * 256) & 0xff;
    srcIndex += 1;
  }

  // 处理salt
  for (let i = 0; i < SALT_LEN;) {
    if (srcIndex < 8) {
      srcBuf[srcIndex] = Math.floor(Math.random() * 256) & 0xff;
      srcIndex += 1;
      i += 1;
    }

    if (srcIndex === 8) {
      // 加密一个块
      encryptBlock(srcBuf, key, ivPlain, ivCrypt, outputBuff, outputPos);
      outputPos += 8;
      srcIndex = 0;
      // 更新 v_crypt 指向刚加密的块
      for (let j = 0; j < 8; j++) {
        ivCrypt[j] = outputBuff[outputPos - 8 + j];
      }
    }
  }

  // 处理 body 数据
  let inputPos = 0;
  while (inputPos < inputLength) {
    if (srcIndex < 8) {
      srcBuf[srcIndex] = data[inputPos];
      srcIndex += 1;
      inputPos += 1;
    }

    if (srcIndex === 8) {
      encryptBlock(srcBuf, key, ivPlain, ivCrypt, outputBuff, outputPos);
      outputPos += 8;
      srcIndex = 0;
      // 更新 ivCrypt 指向刚加密的块
      for (let j = 0; j < 8; j++) {
        ivCrypt[j] = outputBuff[outputPos - 8 + j];
      }
    }
  }

  // 处理 zero 填充
  for (let i = 0; i < ZERO_LEN;) {
    if (srcIndex < 8) {
      srcBuf[srcIndex] = 0;
      srcIndex += 1;
      i += 1 ;
    }

    if (srcIndex === 8) {
      encryptBlock(srcBuf, key, ivPlain, ivCrypt, outputBuff, outputPos);
      outputPos += 8;
      srcIndex = 0;
      // 更新 ivCrypt 指向刚加密的块
      for (let j = 0; j < 8; j++) {
        ivCrypt[j] = outputBuff[outputPos - 8 + j];
      }
    }
  }

  return outputBuff;
}

/**
 * 加密单个 8 字节块
 * @param {Uint8Array} srcBuf - 源数据块
 * @param {Uint8Array} key - 16字节密钥
 * @param {Uint8Array} ivPlain - 明文IV
 * @param {Uint8Array} ivCrypt - 密文IV
 * @param {Uint8Array} outputBuff - 输出缓冲区
 * @param {number} outputPos - 输出位置
 */
function encryptBlock(srcBuf: Uint8Array, key: Uint8Array, ivPlain: Uint8Array, ivCrypt: Uint8Array, outputBuff: Uint8Array, outputPos: number) {
  // 1. 加密前, 异或前 8 个 uint8_t 的密文
  for (let j = 0; j < 8; j++) {
    srcBuf[j] ^= ivCrypt[j];
  }
  // 2. TEA加密
  teaEncrypt(srcBuf, key, outputBuff, outputPos);
  // 3. 加密后, 异或前 8 个 uint8_t 的明文
  for (let j = 0; j < 8; j++) {
    outputBuff[outputPos + j] ^= ivPlain[j];
  }
  // 4. 保存当前的ivPlain
  for (let j = 0; j < 8; j++) {
    ivPlain[j] = srcBuf[j];
  }
}

/**
 * TEA 加密
 * @param {Uint8Array} data - 8 字节数据块
 * @param {Uint8Array} key - 16 字节密钥
 * @param {Uint8Array} output - 输出缓冲区
 * @param {number} outputPos - 输出位置
 */
function teaEncrypt(data: Uint8Array, key: Uint8Array, output: Uint8Array, outputPos: number) {
  let y = readUint32(data, 0);
  let z = readUint32(data, 4);

  const k = [];
  for (let i = 0; i < 4; i++) {
    k[i] = readUint32(key, i * 4);
  }

  let sum = 0;

  for (let i = 0; i < ROUNDS; i++) {
    sum += DELTA;
    sum = sum >>> 0;

    y += ((z << 4) + k[0]) ^ (z + sum) ^ ((z >>> 5) + k[1]);
    y = y >>> 0;
    z += ((y << 4) + k[2]) ^ (y + sum) ^ ((y >>> 5) + k[3]);
    z = z >>> 0;
  }

  writUint32(output, y, outputPos);
  writUint32(output, z, outputPos + 4);
}

